import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as TaskIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import * as workTasksAPI from '../../services/workTasksAPI';
import type { WorkTask } from '../../services/workTasksAPI';

const WorkTasksList: React.FC = () => {
  const [tasks, setTasks] = useState<WorkTask[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  // RBAC Permissions
  const isAdmin = user?.role === 'admin';
  const isBauleiter = user?.role === 'bauleiter';
  const canCreateTasks = isAdmin || isBauleiter;
  const canEditTasks = isAdmin || isBauleiter;
  const canDeleteTasks = isAdmin || isBauleiter;

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const data = await workTasksAPI.getWorkTasks();
      setTasks(data);
    } catch (error) {
      console.error('Error fetching work tasks:', error);
      enqueueSnackbar('Fehler beim Laden der Arbeitsaufträge', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const handleDelete = async (taskId: number) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Arbeitsauftrag löschen möchten?')) {
      return;
    }

    try {
      await workTasksAPI.deleteWorkTask(taskId);
      enqueueSnackbar('Arbeitsauftrag erfolgreich gelöscht', { variant: 'success' });
      fetchTasks();
    } catch (error) {
      console.error('Error deleting work task:', error);
      enqueueSnackbar('Fehler beim Löschen des Arbeitsauftrags', { variant: 'error' });
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'hoch':
        return 'error';
      case 'mittel':
        return 'warning';
      case 'niedrig':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'neu':
        return 'primary';
      case 'erledigt':
        return 'success';
      default:
        return 'default';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'hoch':
        return 'Hoch';
      case 'mittel':
        return 'Mittel';
      case 'niedrig':
        return 'Niedrig';
      default:
        return priority;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'neu':
        return 'Neu';
      case 'erledigt':
        return 'Erledigt';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <TaskIcon fontSize="large" color="primary" />
          <Typography variant="h4" component="h1">
            Arbeitsaufträge
          </Typography>
        </Box>
        {canCreateTasks && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/work-tasks/new')}
          >
            Neuer Arbeitsauftrag
          </Button>
        )}
      </Box>

      {tasks.length === 0 ? (
        <Alert severity="info">
          Keine Arbeitsaufträge gefunden.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {tasks.map((task) => (
            <Grid item xs={12} md={6} lg={4} key={task.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="h3" noWrap>
                      {task.title}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Chip
                        label={getStatusLabel(task.status)}
                        color={getStatusColor(task.status) as any}
                        size="small"
                      />
                      <Chip
                        label={getPriorityLabel(task.priority)}
                        color={getPriorityColor(task.priority) as any}
                        size="small"
                      />
                    </Box>
                  </Box>

                  {task.description && (
                    <Typography variant="body2" color="text.secondary" mb={2} noWrap>
                      {task.description}
                    </Typography>
                  )}

                  <Box mb={2}>
                    <Typography variant="caption" color="text.secondary">
                      Projekt: {task.project_name}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      Zugewiesen an: {task.assigned_to_name}
                    </Typography>
                    {task.due_date && (
                      <>
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          Fällig: {format(new Date(task.due_date), 'dd.MM.yyyy', { locale: de })}
                        </Typography>
                      </>
                    )}
                  </Box>

                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/work-tasks/${task.id}`)}
                      title="Anzeigen"
                    >
                      <ViewIcon />
                    </IconButton>
                    {canEditTasks && (
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/work-tasks/${task.id}/edit`)}
                        title="Bearbeiten"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {canDeleteTasks && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(task.id)}
                        title="Löschen"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default WorkTasksList;
